import React, { useCallback, useEffect, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import { Link as RouterLink } from '~/lib/react-router-dom';

import { Box, Button, Grid, Link, TextField } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import GridPagination from '~/components/Common/Elements/GridPagination';
import PageSubheader from '~/components/Common/Elements/PageSubheader';

import useAppNavigation from '~/hooks/useAppNavigation';
import useQuery from '~/hooks/useQuery';

import { HotelBidHistoryRecord, getHotelBidHistory } from '~/services/PublicOfferFeedService';

import dateFormatter from '~/utils/dateFormatter';

interface FormValues {
  customerId?: string;
  campaignId?: string;
  adGroupId?: string;
  hotelId?: string;
  search?: string;
}

interface QueryParams extends FormValues {
  page_num: number;
  page_size: number;
}

const columns: Array<GridColDef> = [
  {
    field: 'hotelId',
    headerName: 'Property Id',
    flex: 2,
  },
  {
    field: 'campaignId',
    headerName: 'Campaign Id',
    flex: 1,
    renderCell: (params) => (
      <Link
        component={RouterLink}
        to={`/marketing/google-ads/customer/${params.row.customerId}/campaign/${params.row.campaignId}`}
        underline="hover"
      >
        {params.row.campaignId}
      </Link>
    ),
  },
  {
    field: 'adGroupId',
    headerName: 'Ad Group Id',
    flex: 1,
    renderCell: (params) => (
      <Link
        component={RouterLink}
        to={`/marketing/google-ads/customer/${params.row.customerId}/campaign/${params.row.campaignId}/ad-group/${params.row.adGroupId}`}
        underline="hover"
      >
        {params.row.adGroupId}
      </Link>
    ),
  },
  {
    field: 'bidTarget',
    headerName: 'Target',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'previousBidTarget',
    headerName: 'Previous Target',
    flex: 1,
    align: 'right',
    headerAlign: 'right',
  },
  {
    field: 'biddingStrategy',
    headerName: 'bidding Strategy',
    align: 'right',
    headerAlign: 'right',
    flex: 1,
  },
  {
    field: 'changedBy',
    headerName: 'Changed By',
    flex: 2,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => params.row.changedBy ?? 'Daily Automation',
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params) => dateFormatter(params.row.updatedAt),
  },
];

function HotelBidModificationHistory() {
  const query = useQuery();
  const { enqueueSnackbar } = useSnackbar();
  const [data, setData] = useState<Array<HotelBidHistoryRecord>>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pageNum, setPageNum] = useState(Number(query.get('page_num') ?? 0));
  const [pageSize, setPageSize] = useState(Number(query.get('page_size') ?? 100));
  const [totalCount, setTotalCount] = useState(0);
  const { push } = useAppNavigation();

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<FormValues>({
    defaultValues: {
      customerId: query.get('customerId') || '',
      campaignId: query.get('campaignId') || '',
      adGroupId: query.get('adGroupId') || '',
      hotelId: query.get('hotelId') || '',
      search: query.get('search') || '',
    },
  });

  const setQueryParams = useCallback(() => {
    const searchParams = qs.stringify({
      page_num: pageNum,
      page_size: pageSize,
      customerId: getValues('customerId'),
      campaignId: getValues('campaignId'),
      adGroupId: getValues('adGroupId'),
      hotelId: getValues('hotelId'),
      search: getValues('search'),
    } as QueryParams);

    push({
      search: `?${searchParams}`,
    });
  }, [getValues, pageNum, pageSize, push]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);

    try {
      const formValues = getValues();
      const { result, count } = await getHotelBidHistory({
        customerId: formValues.customerId || '',
        campaignId: formValues.campaignId || '',
        adGroupId: formValues.adGroupId || '',
        hotelId: formValues.hotelId || '',
        search: formValues.search || '',
        page: pageNum,
        limit: pageSize,
      });

      setData(result);
      setTotalCount(Number(count));
      setQueryParams();
    } catch (error) {
      enqueueSnackbar(error.message);
    } finally {
      setIsLoading(false);
    }
  }, [pageNum, pageSize, getValues, setQueryParams, enqueueSnackbar]);

  useEffect(() => {
    fetchData();
  }, []);

  const handleSearch = useCallback(async () => {
    fetchData();
  }, [fetchData]);

  const onPageChange = useCallback(
    (page: number, pageSize: number) => {
      setPageNum(page);
      setPageSize(pageSize);
    },
    [fetchData, query],
  );

  useEffect(() => {
    fetchData();
  }, [pageNum, pageSize, fetchData]);

  return (
    <Box sx={{ m: 3 }}>
      <Helmet>
        <title>Bid Modification History</title>
      </Helmet>

      <PageSubheader title="Search Hotel Bid Modification History" />

      <Grid
        container
        spacing={2}
        justifyContent="flex-start"
        alignItems="center"
        component="form"
        onSubmit={handleSubmit(handleSearch)}
      >
        <Grid>
          <TextField
            variant="outlined"
            label="Customer ID"
            {...register('customerId')}
            error={!!errors.customerId}
            helperText={errors.customerId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid>
          <TextField
            variant="outlined"
            label="Campaign ID"
            {...register('campaignId')}
            error={!!errors.campaignId}
            helperText={errors.campaignId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid>
          <TextField
            variant="outlined"
            label="Ad Group ID"
            {...register('adGroupId')}
            error={!!errors.adGroupId}
            helperText={errors.adGroupId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid>
          <TextField
            variant="outlined"
            label="Hotel ID"
            {...register('hotelId')}
            error={!!errors.hotelId}
            helperText={errors.hotelId?.message}
            sx={{ width: 300 }}
          />
        </Grid>
        <Grid>
          <TextField
            variant="outlined"
            label="Search"
            {...register('search')}
            error={!!errors.search}
            helperText={errors.search?.message}
            sx={{ width: 300 }}
          />
        </Grid>

        <Grid>
          <Button variant="contained" color="primary" type="submit">
            Search
          </Button>
        </Grid>
      </Grid>

      <Box mt={2}>
        <DataGrid
          slots={{ pagination: GridPagination }}
          onPaginationModelChange={({ page, pageSize }) => onPageChange(page, pageSize)}
          loading={isLoading}
          rows={data}
          columns={columns}
          autoHeight
          rowCount={totalCount}
          paginationMode="server"
          pageSizeOptions={[100, 250, 500]}
          paginationModel={{ page: pageNum, pageSize: pageSize }}
        />
      </Box>
    </Box>
  );
}

export default HotelBidModificationHistory;
